// services/msal.ts
import {
  type AccountInfo,
  type Configuration,
  EventType,
  InteractionRequiredAuthError,
  PublicClientApplication,
} from '@azure/msal-browser';
import { env } from '../env';

let msalInitPromise: Promise<PublicClientApplication> | null = null;

function buildMsalConfig(): Configuration {
  const clientId = env.CLIENT_ID;
  const authority = env.AUTHORITY;
  const redirectUri = env.REDIRECT_URI;

  if (!clientId || !authority || !redirectUri) {
    throw new Error(
      `MSAL config missing values: clientId=${clientId} authority=${authority} redirectUri=${redirectUri}`,
    );
  }

  return {
    auth: {
      clientId,
      authority,
      redirectUri,
      // Helps avoid weird “return to previous URL” behavior in some setups
      navigateToLoginRequestUrl: false,
    },
    cache: {
      cacheLocation: 'localStorage',
      // If you have Safari/ITP issues, set this true:
      // storeAuthStateInCookie: true,
      storeAuthStateInCookie: false,
    },
    system: {
      tokenRenewalOffsetSeconds: 300,
    },
  };
}

export async function getMsalInstance(): Promise<PublicClientApplication> {
  if (!msalInitPromise) {
    msalInitPromise = (async () => {
      const instance = new PublicClientApplication(buildMsalConfig());
      await instance.initialize();

      instance.addEventCallback((event) => {
        if (
          event.eventType === EventType.LOGIN_SUCCESS ||
          event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
        ) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const account = (event.payload as any)?.account as AccountInfo | undefined;
          if (account) instance.setActiveAccount(account);
        }
      });

      return instance;
    })();
  }
  return msalInitPromise;
}

export async function ensureActiveAccount(): Promise<AccountInfo | null> {
  const msal = await getMsalInstance();
  let account = msal.getActiveAccount();
  if (!account) {
    const accounts = msal.getAllAccounts();
    if (accounts.length) {
      account = accounts[0];
      msal.setActiveAccount(account);
    }
  }
  return account ?? null;
}

/** ===== Token helpers ===== */

type CacheKey = string; // `${homeAccountId}|${scopes.sort().join(' ')}`
type TokenEntry = { accessToken: string; expiresOn?: Date | null };

const tokenCache = new Map<CacheKey, TokenEntry>();
const inflight = new Map<CacheKey, Promise<string | null>>();
const SKEW_MS = 2 * 60_000;

function keyFor(account: AccountInfo, scopes: string[]) {
  return `${account.homeAccountId}|${[...scopes].sort().join(' ')}`;
}
function isFresh(entry?: TokenEntry) {
  if (!entry?.expiresOn) return false;
  return entry.expiresOn.getTime() - SKEW_MS > Date.now();
}

/**
 * Best-effort token getter:
 * - Silent token if possible (auto-refresh via refresh token / hidden iframe)
 * - If interaction is required, return null (UI layer should trigger redirect)
 */
export async function getAccessToken(
  scopes: string[] = [env.CLIENT_SCOPE],
  forceRefresh = false,
): Promise<string | null> {
  const msal = await getMsalInstance();
  const account = await ensureActiveAccount();
  if (!account) return null;

  const cacheKey = keyFor(account, scopes);

  if (!forceRefresh) {
    const entry = tokenCache.get(cacheKey);
    if (isFresh(entry)) return entry?.accessToken ?? ''
  }

  const existing = inflight.get(cacheKey);
  if (existing) return existing;

  const p = (async () => {
    try {
      const result = await msal.acquireTokenSilent({
        account,
        scopes,
        forceRefresh,
      });

      tokenCache.set(cacheKey, {
        accessToken: result.accessToken,
        expiresOn: result.expiresOn ?? null,
      });

      return result.accessToken;
    } catch (e) {
      const name = (e as Error)?.name;
      if (e instanceof InteractionRequiredAuthError || name === 'InteractionRequiredAuthError') {
        // ✅ Don’t redirect from here. Let UI / route guard trigger interactive login.
        return null;
      }
      throw e;
    } finally {
      inflight.delete(cacheKey);
    }
  })();

  inflight.set(cacheKey, p);
  return p.catch(() => null);
}
